import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    listarTiposTurno,
    crearTipoTurno,
    actualizarTipoTurno,
    eliminarTipoTurno,
} from '../../services/tipoTurnoService';
import MenuAccionesFila from './MenuAccionesFila';
import '../../style/components/GestionDatosMaestros.css';

/**
 * Catálogo de tipos de turno: a qué vino la persona.
 *
 * No confundir con el área de atención (quién la atiende, se decide en el check-in) ni con el
 * tipo de trámite (el módulo del sistema, solo si se abre carpeta). Son tres cosas
 * independientes: un turno de "Cobro" no mapea a ningún trámite.
 *
 * Vive acá y no en el código porque la lista la maneja el estudio y cambia.
 */
const FORM_VACIO = {
    etiqueta: '',
    prefijo: '',
    color: '#64748b',
    colorFondo: '#f1f5f9',
    orden: 100,
};

export default function GestionTiposTurno() {
    const [tipos, setTipos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [form, setForm] = useState(FORM_VACIO);
    const [editando, setEditando] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [aEliminar, setAEliminar] = useState(null);
    const formularioRef = useRef(null);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            setTipos(await listarTiposTurno(false));
        } catch (e) {
            toast.error('No se pudieron cargar los tipos de turno.');
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const abrirNuevo = () => {
        setForm(FORM_VACIO);
        setEditando(null);
    };

    const abrirEdicion = (tipo) => {
        setForm({
            etiqueta: tipo.etiqueta,
            prefijo: tipo.prefijo,
            color: tipo.color,
            colorFondo: tipo.colorFondo,
            orden: tipo.orden,
        });
        setEditando(tipo);
        // El formulario está arriba de la tabla: editando una fila de abajo (son más de 20
        // tipos) quedaba fuera de vista y parecía que Editar no hacía nada.
        // Diferido: al cerrarse, el menú ⋮ devuelve el foco a su botón y eso cancelaba el scroll.
        setTimeout(() => {
            const formulario = formularioRef.current;
            formulario?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
            formulario?.querySelector('input')?.focus({ preventScroll: true });
        }, 0);
    };

    const handleGuardar = async (e) => {
        e.preventDefault();
        if (!form.etiqueta.trim()) {
            toast.error('El nombre es obligatorio.');
            return;
        }
        setGuardando(true);
        try {
            if (editando) {
                await actualizarTipoTurno(editando.id, form);
                toast.success('Tipo de turno actualizado.');
            } else {
                await crearTipoTurno(form);
                toast.success('Tipo de turno creado.');
            }
            setForm(FORM_VACIO);
            setEditando(null);
            await cargar();
        } catch (err) {
            toast.error(err?.response?.data?.mensaje || 'No se pudo guardar.');
        } finally {
            setGuardando(false);
        }
    };

    const handleActivo = async (tipo) => {
        try {
            await actualizarTipoTurno(tipo.id, { activo: !tipo.activo });
            await cargar();
        } catch (err) {
            toast.error(err?.response?.data?.mensaje || 'No se pudo cambiar el estado.');
        }
    };

    // Si los turnos de este tipo entran en las estadísticas de Recepción. Los de quien viene por otra
    // cosa que una consulta nueva (cobro, turno médico, ANSES...) quedan fuera de todos los reportes.
    const handleEstadisticas = async (tipo) => {
        try {
            await actualizarTipoTurno(tipo.id, { cuentaEnEstadisticas: !tipo.cuentaEnEstadisticas });
            await cargar();
        } catch (err) {
            toast.error(err?.response?.data?.mensaje || 'No se pudo cambiar.');
        }
    };

    const handleEliminar = async () => {
        try {
            await eliminarTipoTurno(aEliminar.id);
            toast.success('Tipo de turno eliminado.');
            setAEliminar(null);
            await cargar();
        } catch (err) {
            // El backend rechaza el borrado si el tipo tiene turnos y explica por qué:
            // se muestra su mensaje tal cual, que dice cuántos son.
            toast.error(err?.response?.data?.mensaje || 'No se pudo eliminar.');
            setAEliminar(null);
        }
    };

    return (
        <div className="gestion-datos-maestros">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                <div>
                    <p className="text-muted small mb-0" style={{ maxWidth: 620 }}>
                        A qué vino la persona. Es distinto del <strong>área de atención</strong> (quién
                        la atiende, se elige al hacer el check-in) y del <strong>tipo de trámite</strong> (el
                        módulo del sistema, solo si se abre carpeta).
                    </p>
                    <p className="text-muted small mb-0 mt-1" style={{ maxWidth: 620 }}>
                        <strong>Estadísticas:</strong> los tipos que no cuentan (por ejemplo cobros o turnos
                        médicos) quedan fuera de todos los reportes de Recepción, igual que los turnos de ANSES.
                    </p>
                </div>
                {editando && (
                    <Button variant="outline-secondary" size="sm" onClick={abrirNuevo}>
                        <i className="bi bi-plus-lg me-1" />Nuevo en vez de editar
                    </Button>
                )}
            </div>

            {/* Alta / edición */}
            <Form ref={formularioRef} onSubmit={handleGuardar} className="mb-4 p-3 border rounded-3 bg-white shadow-sm">
                <div className="row g-2 align-items-end">
                    <div className="col-md-4">
                        <Form.Label className="small fw-semibold mb-1">
                            Nombre {editando && <span className="text-muted">(editando)</span>}
                        </Form.Label>
                        <Form.Control
                            value={form.etiqueta}
                            onChange={(e) => setForm({ ...form, etiqueta: e.target.value })}
                            placeholder="Ej: Junta médica"
                        />
                    </div>
                    <div className="col-md-2">
                        <Form.Label className="small fw-semibold mb-1">Prefijo</Form.Label>
                        <Form.Control
                            value={form.prefijo}
                            maxLength={5}
                            onChange={(e) => setForm({ ...form, prefijo: e.target.value.toUpperCase() })}
                            placeholder="JMD"
                        />
                        <Form.Text className="text-muted" style={{ fontSize: '0.72rem' }}>
                            Va en el número que ve el cliente
                        </Form.Text>
                    </div>
                    <div className="col-md-2">
                        <Form.Label className="small fw-semibold mb-1">Orden</Form.Label>
                        <Form.Control
                            type="number"
                            value={form.orden}
                            onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })}
                        />
                    </div>
                    <div className="col-md-2">
                        <Form.Label className="small fw-semibold mb-1">Colores</Form.Label>
                        <div className="d-flex gap-1">
                            <Form.Control
                                type="color"
                                title="Color del texto"
                                value={form.color}
                                onChange={(e) => setForm({ ...form, color: e.target.value })}
                            />
                            <Form.Control
                                type="color"
                                title="Color de fondo"
                                value={form.colorFondo}
                                onChange={(e) => setForm({ ...form, colorFondo: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="col-md-2">
                        <Button type="submit" className="w-100" disabled={guardando}>
                            {guardando
                                ? <Spinner animation="border" size="sm" />
                                : <><i className="bi bi-check-lg me-1" />{editando ? 'Guardar' : 'Agregar'}</>}
                        </Button>
                    </div>
                </div>

                {/* Vista previa: el badge se ve igual que en el turnero. */}
                <div className="mt-2">
                    <span className="text-muted small me-2">Se va a ver así:</span>
                    <span
                        className="badge rounded-pill"
                        style={{ background: form.colorFondo, color: form.color, border: `1px solid ${form.color}33` }}
                    >
                        {form.etiqueta || 'Nombre del tipo'}
                    </span>
                </div>
            </Form>

            {cargando ? (
                <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
            ) : (
                <Table hover responsive className="align-middle datos-maestros-table">
                    <thead>
                        <tr>
                            <th>Tipo</th>
                            <th>Código</th>
                            <th>Prefijo</th>
                            <th>Orden</th>
                            <th>Estado</th>
                            <th title="Si los turnos de este tipo entran en las estadísticas de Recepción">Estadísticas</th>
                            <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tipos.map((t) => (
                            <tr key={t.id} style={{ opacity: t.activo ? 1 : 0.55 }}>
                                <td>
                                    <span
                                        className="badge rounded-pill"
                                        style={{ background: t.colorFondo, color: t.color, border: `1px solid ${t.color}33` }}
                                    >
                                        {t.etiqueta}
                                    </span>
                                    {t.esSistema && (
                                        <span className="badge bg-light text-secondary ms-2" title="Lo asigna el sistema">
                                            sistema
                                        </span>
                                    )}
                                </td>
                                <td><code className="small text-muted">{t.codigo}</code></td>
                                <td><span className="small">{t.prefijo}</span></td>
                                <td><span className="small text-muted">{t.orden}</span></td>
                                <td>
                                    <Form.Check
                                        type="switch"
                                        checked={t.activo}
                                        // Los de sistema no se desactivan: los asigna el propio
                                        // sistema y siempre tiene que haber uno.
                                        disabled={t.esSistema}
                                        onChange={() => handleActivo(t)}
                                        label={<span className="small">{t.activo ? 'Activo' : 'Inactivo'}</span>}
                                    />
                                </td>
                                <td>
                                    <Form.Check
                                        type="switch"
                                        checked={t.cuentaEnEstadisticas !== false}
                                        onChange={() => handleEstadisticas(t)}
                                        label={<span className="small">{t.cuentaEnEstadisticas !== false ? 'Cuenta' : 'No cuenta'}</span>}
                                    />
                                </td>
                                <td className="text-end">
                                    <MenuAccionesFila acciones={[
                                        { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => abrirEdicion(t) },
                                        {
                                            etiqueta: 'Eliminar',
                                            icono: 'bi-trash',
                                            peligro: true,
                                            deshabilitado: t.esSistema,
                                            motivo: 'Tipo del sistema: no se puede eliminar',
                                            onClick: () => setAEliminar(t),
                                        },
                                    ]} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={!!aEliminar} onHide={() => setAEliminar(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Eliminar tipo de turno</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Eliminar <strong>{aEliminar?.etiqueta}</strong>?
                    <p className="text-muted small mb-0 mt-2">
                        Si ya hay turnos con este tipo no se va a poder borrar, porque quedarían
                        apuntando a un tipo que no existe. En ese caso conviene desactivarlo: deja
                        de ofrecerse al cargar un turno y el histórico queda intacto.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setAEliminar(null)}>Cancelar</Button>
                    <Button variant="danger" onClick={handleEliminar}>Eliminar</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
